import { useNode } from "@craftjs/core";
import React from "react";
import { Box, Typography, ToggleButton, ToggleButtonGroup } from "@mui/material";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import { selectedOutlineSx } from "../../config/settingsStyles";
import { TextField, TextareaField, SliderField, CheckboxField, SettingsSection } from "../ui/form/SettingsField";

interface GoogleMapProps {
  embedUrl?: string;
  address?: string;
  height?: number;
  width?: number;
  align?: "left" | "center" | "right";
  borderRadius?: number;
  showAddress?: boolean;
  maxWidth?: number;
  paddingX?: number;
  paddingTop?: number;
  paddingBottom?: number;
}

export const GoogleMap = ({
  embedUrl = "",
  address = "Bangkok, Thailand",
  height = 400,
  width = 100,
  align = "center",
  borderRadius = 8,
  showAddress = true,
  maxWidth = 0,
  paddingX = 16,
  paddingTop = 16,
  paddingBottom = 16,
}: GoogleMapProps) => {
  const {
    connectors: { connect, drag },
    selected,
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  return (
    <Box
      ref={(ref: HTMLDivElement | null) => {
        if (ref) connect(drag(ref));
      }}
      sx={{
        px: `${paddingX}px`,
        pt: `${paddingTop}px`,
        pb: `${paddingBottom}px`,
        width: `${width}%`,
        ...(maxWidth > 0 && { maxWidth: `${maxWidth}px` }),
        ml: align === "center" ? "auto" : align === "right" ? "auto" : 0,
        mr: align === "center" ? "auto" : align === "left" ? "auto" : 0,
        ...(selected && selectedOutlineSx),
      }}
    >
      {showAddress && address && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, color: "#374151" }}>
          <Typography component="span" sx={{ fontSize: "1.25rem" }}>
            📍
          </Typography>
          <Typography>{address}</Typography>
        </Box>
      )}

      <Box
        sx={{
          overflow: "hidden",
          bgcolor: "#f3f4f6",
          height: `${height}px`,
          borderRadius: `${borderRadius}px`,
        }}
      >
        {embedUrl ? (
          <Box
            component="iframe"
            src={embedUrl}
            sx={{
              width: "100%",
              height: "100%",
              border: 0,
            }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#6b7280",
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <Typography component="span" sx={{ fontSize: "2.25rem", display: "block", mb: 1 }}>
                🗺️
              </Typography>
              <Typography>Add Google Maps embed URL in settings</Typography>
              <Typography sx={{ fontSize: "0.875rem", mt: 1 }}>Go to Google Maps → Share → Embed a map</Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// Extract src URL from iframe tag if user pastes full embed code
const extractSrcFromIframe = (input: string): string => {
  const trimmed = input.trim();
  // Check if input contains iframe tag
  if (trimmed.toLowerCase().includes("<iframe")) {
    // Extract src attribute using regex
    const srcMatch = trimmed.match(/src=["']([^"']+)["']/i);
    if (srcMatch && srcMatch[1]) {
      return srcMatch[1];
    }
  }
  return trimmed;
};

const GoogleMapSettings = () => {
  const {
    actions: { setProp },
    embedUrl,
    address,
    height,
    width,
    align,
    borderRadius,
    showAddress,
    maxWidth,
    paddingX,
    paddingTop,
    paddingBottom,
  } = useNode((node) => ({
    embedUrl: node.data.props.embedUrl,
    address: node.data.props.address,
    height: node.data.props.height,
    width: node.data.props.width,
    align: node.data.props.align,
    borderRadius: node.data.props.borderRadius,
    showAddress: node.data.props.showAddress,
    maxWidth: node.data.props.maxWidth,
    paddingX: node.data.props.paddingX,
    paddingTop: node.data.props.paddingTop,
    paddingBottom: node.data.props.paddingBottom,
  }));

  const handleEmbedUrlChange = (value: string) => {
    const extractedUrl = extractSrcFromIframe(value);
    setProp((props: GoogleMapProps) => (props.embedUrl = extractedUrl));
  };

  return (
    <SettingsSection>
      <div>
        <TextareaField
          label="Google Maps Embed URL"
          value={embedUrl || ""}
          onChange={handleEmbedUrlChange}
          placeholder="Paste iframe embed code or URL..."
          rows={3}
        />
        <p style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: "4px" }}>
          Paste full iframe code or just the URL - we'll extract it automatically
        </p>
      </div>

      <TextField
        label="Address"
        value={address || ""}
        onChange={(v) => setProp((props: GoogleMapProps) => (props.address = v))}
      />

      <CheckboxField
        label="Show Address"
        checked={showAddress !== false}
        onChange={(v) => setProp((props: GoogleMapProps) => (props.showAddress = v))}
      />

      <SliderField
        label="Width"
        value={width || 100}
        onChange={(v) => setProp((props: GoogleMapProps) => (props.width = v))}
        min={30}
        max={100}
        unit="%"
      />

      <Box>
        <Typography variant="body2" gutterBottom>
          Alignment
        </Typography>
        <ToggleButtonGroup
          value={align || "center"}
          exclusive
          onChange={(_, value) => {
            if (value !== null) {
              setProp((props: GoogleMapProps) => (props.align = value as GoogleMapProps["align"]));
            }
          }}
          size="small"
          fullWidth
        >
          <ToggleButton value="left" sx={{ flex: 1 }}>
            <FormatAlignLeftIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="center" sx={{ flex: 1 }}>
            <FormatAlignCenterIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="right" sx={{ flex: 1 }}>
            <FormatAlignRightIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <SliderField
        label="Height"
        value={height || 400}
        onChange={(v) => setProp((props: GoogleMapProps) => (props.height = v))}
        min={200}
        max={600}
        unit="px"
      />

      <SliderField
        label="Border Radius"
        value={borderRadius || 8}
        onChange={(v) => setProp((props: GoogleMapProps) => (props.borderRadius = v))}
        min={0}
        max={24}
        unit="px"
      />

      <SliderField
        label="Max Width"
        value={maxWidth || 0}
        onChange={(v) => setProp((props: GoogleMapProps) => (props.maxWidth = v))}
        min={0}
        max={1600}
        unit="px"
      />

      <SliderField
        label="Padding X"
        value={paddingX || 16}
        onChange={(v) => setProp((props: GoogleMapProps) => (props.paddingX = v))}
        min={0}
        max={120}
        unit="px"
      />

      <SliderField
        label="Padding Top"
        value={paddingTop || 16}
        onChange={(v) => setProp((props: GoogleMapProps) => (props.paddingTop = v))}
        min={0}
        max={120}
        unit="px"
      />

      <SliderField
        label="Padding Bottom"
        value={paddingBottom || 16}
        onChange={(v) => setProp((props: GoogleMapProps) => (props.paddingBottom = v))}
        min={0}
        max={120}
        unit="px"
      />
    </SettingsSection>
  );
};

GoogleMap.craft = {
  displayName: "Google Map",
  props: {
    embedUrl: "",
    address: "Bangkok, Thailand",
    height: 400,
    width: 100,
    align: "center",
    borderRadius: 8,
    showAddress: true,
    maxWidth: 0,
    paddingX: 16,
    paddingTop: 16,
    paddingBottom: 16,
  },
  related: {
    settings: GoogleMapSettings,
  },
};
